import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import type { SpIndexJob } from '../lib/graphSites'

/**
 * "Indexation result" — a settled click-to-index job's full detail: every file the run touched,
 * with its outcome and (for a skip or a failure) why. The bottom bar only ever has room for the
 * run's own counts (`{ingested} indexed · {skipped} skipped · {failed} failed`); this is where a
 * member goes to find out WHICH file failed and WHY, rather than only how many.
 *
 * Reuses the generic modal shell (`.modal-backdrop`/`.modal`/`.modal-head`/`.modal-body`) and
 * ConfirmModal's own focus handling — focus the close button on open, restore on close, Escape
 * dismisses — rather than a new dialog primitive. `role="dialog"`, not `alertdialog`: nothing here
 * asks for a decision, it only reports one that already happened.
 */
/** What the modal shows of a job, also in the bid home's job details (JobDetailsDialog.tsx). */
export function IndexResultBody({ job }: { job: SpIndexJob }) {
  const { t } = useLang()
  return (
    <>
      {job.result && (
        <p className="spb-result-summary">
          {t('bidsite_index_done')
            .replace('{ingested}', String(job.result.ingested))
            .replace('{skipped}', String(job.result.skipped))
            .replace('{failed}', String(job.result.failed))}
        </p>
      )}
      {job.error && (
        <p className="spb-error" role="alert">
          {job.error}
        </p>
      )}
      {job.progress.length === 0 ? (
        <p className="spb-note">{t('bidsite_index_result_empty')}</p>
      ) : (
        <ul className="spb-result-list">
          {job.progress.map((p, i) => (
            <li key={`${p.name}-${i}`} className={`spb-result-row spb-result-${p.outcome}`}>
              <span className="spb-result-name">{p.name}</span>
              <span className="spb-result-outcome">{t(`bidsite_row_${p.outcome}` as 'bidsite_row_ingested')}</span>
              {p.reason && <span className="spb-result-reason">{p.reason}</span>}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

export function IndexResultModal({ job, onClose }: { job: SpIndexJob; onClose: () => void }) {
  const { t } = useLang()
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => prev?.focus()
  }, [])

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        className="modal spb-result-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spb-result-title"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
      >
        <div className="modal-head">
          <h2 id="spb-result-title">{t('bidsite_index_result_link')}</h2>
          <button ref={closeRef} className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <IndexResultBody job={job} />
        </div>
      </div>
    </div>,
    document.body,
  )
}
